import React, { Dispatch, SetStateAction, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ownerSelector, prospectsSelector, RootState } from "../../store/store";
import { MdClose, MdDelete } from "react-icons/md";
import {
  setListDescription,
  setListName,
  setProspects,
  setSelectedProspects,
} from "../../store/slices/prospectsSlice";
import { AiOutlineEnter, AiOutlineLoading3Quarters } from "react-icons/ai";
import axios from "../../utils/axios";
import { toast } from "react-toastify";
import { setAllProspectsList, setListId, setLists } from "../../store/slices/ownerSlice";
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import { addTagToProspect } from "../../store/features/ownerActions";
import { fetchLists, fetchProspects } from "../../utils";
import TagSwatch from "../tags/TagSwatch";
import { Button } from "../ui";
type Props = {
  setSelectedArray: Dispatch<SetStateAction<string[]>>;
};

const normalizeMongoIdString = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object" && value !== null && "_id" in value) {
    const inner = (value as { _id?: unknown })._id;
    return inner != null ? String(inner).trim() : "";
  }
  return String(value).trim();
};

const isLikelyMongoObjectId = (id: string) => /^[a-f\d]{24}$/i.test(id);

const ProspectsSettings = (props: Props) => {
  const {
    lists,
    listId,
    tags,
    owner,
    currentWorkspace,
    currentUser,
    allProspectsList,
  } =
    useSelector(ownerSelector);
  const workspace =
    typeof currentWorkspace === "string"
      ? (() => {
          try {
            return JSON.parse(currentWorkspace || "null");
          } catch {
            return null;
          }
        })()
      : currentWorkspace;
  const workspaceId = workspace?._id as string;
  const userId =
    normalizeMongoIdString(currentUser?.userId as unknown) ||
    normalizeMongoIdString((owner as { userId?: unknown })?.userId);
  const ownerId = normalizeMongoIdString(owner?._id as unknown);
  const { selectedProspects, listName } = useSelector(prospectsSelector);
  const dispatch: ThunkDispatch<RootState, undefined, Action> = useDispatch();
  const [showMove, setShowMove] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [moving, setMoving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const availableMoveLists = (lists || []).filter(
    (list: any) =>
      list.listName !== listName &&
      !list.isDeleted &&
      list.inActiveCampaign === false &&
      list.listName !== "All Prospects"
  );

  const handleProspectMove = async (currentId: string) => {
    setMoving(true);
    // console.log(currentId);
    try {
      const res = await axios.post("/prospect/addProspectToList", {
        ownerId: ownerId || undefined,
        userId,
        workspaceId,
        listId: currentId,
        prospectIds: selectedProspects,
        removeListId: listId,
      });
      if (res.status === 200) {
        // console.log(res.data);
        setMoving(false);
        const lists = await fetchLists(
          owner?._id as string,
          workspaceId,
          userId
        );
        dispatch(setLists(lists));
        dispatch(setListName(res.data.listName));
        dispatch(setListDescription(res.data.description));
        dispatch(setListId(res.data._id));
        dispatch(setSelectedProspects([]));
        props.setSelectedArray([]);
        toast.success("Prospects moved successfully");
      }
    } catch (error) {
      toast.error("An error occurred while moving prospects");
      console.error(error);
    }
  };

  const handleProspectDelete = async () => {
    setDeleting(true);
    const loading = toast.loading("Deleting prospects...");
    try {
      const workspaceId =
        typeof currentWorkspace === "string"
          ? (() => {
              try {
                return JSON.parse(currentWorkspace)?._id || "";
              } catch {
                return "";
              }
            })()
          : currentWorkspace?._id || "";

      const allListFromState = (lists || []).find(
        (list: any) =>
          typeof list?.listName === "string" &&
          list.listName.trim().toLowerCase() === "all prospects"
      );

      let resolvedAllProspectsListId =
        allProspectsList?._id && allProspectsList._id !== "all"
          ? allProspectsList._id
          : allListFromState?._id;

      // Fallback: refresh lists once so Redux has the real All Prospects row when possible.
      if (!resolvedAllProspectsListId && ownerId && workspaceId) {
        const refreshedLists = await fetchLists(ownerId, workspaceId, userId);
        const asArray = Array.isArray(refreshedLists) ? refreshedLists : [];
        dispatch(setLists(asArray));
        const fetchedAllList = asArray.find(
          (list: any) =>
            typeof list?.listName === "string" &&
            list.listName.trim().toLowerCase() === "all prospects"
        );
        if (fetchedAllList) {
          dispatch(setAllProspectsList(fetchedAllList));
          resolvedAllProspectsListId = fetchedAllList._id;
        }
      }

      const deleteBody: Record<string, unknown> = {
        ownerId: ownerId || undefined,
        userId,
        workspaceId,
        listId,
        prospectIds: selectedProspects,
      };
      const candidateAllId = String(resolvedAllProspectsListId || "").trim();
      if (candidateAllId && candidateAllId !== "all" && isLikelyMongoObjectId(candidateAllId)) {
        deleteBody.allProspectsListId = candidateAllId;
      }

      const res = await axios.post("/prospect/deleteProspectsFromList", deleteBody);
      if (res.status === 200) {
        console.log(res.data);
        setDeleting(false);
        dispatch(setListName(res.data.listName));
        dispatch(setListId(res.data._id));
        dispatch(setProspects(res.data.prospects));
        dispatch(setSelectedProspects([]));
        props.setSelectedArray([]);
        toast.dismiss(loading);
        toast.success("Prospects deleted successfully");
      }
    } catch (error) {
      setDeleting(false);
      toast.dismiss(loading);
      toast.error("An error occurred while deleting prospects");
      console.error(error);
    }
  };

  const handleBulkTagging = async (tagId: string) => {
    try {
      const res = await dispatch(
        addTagToProspect({
          prospectIds: selectedProspects,
          tagId,
          listId,
          userId,
          workspaceId,
        })
      );
      if (res.payload.success) {
        const list = await fetchProspects(
          owner?._id as string,
          listId,
          1,
          10,
          userId,
          workspaceId
        );
        dispatch(setProspects(list.prospects));
        dispatch(setSelectedProspects([]));
        props.setSelectedArray([]);
        toast.success("Tag added to prospects successfully");
      } else if (!res.payload.success) {
        toast.error(res.payload.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    }
  };
  return (
    <div className="absolute left-1/2 top-2 z-50 flex -translate-x-1/2 items-center gap-4 rounded-[8px] border border-light-grey bg-white px-4 py-2 text-xs shadow-lg">
      <span
        className="cursor-pointer rounded-[6px] p-1 text-dark-grey hover:bg-primary"
        onClick={() => {
          dispatch(setSelectedProspects([]));
          props.setSelectedArray([]);
        }}
      >
        <MdClose size={16} />
      </span>
      <p className="text-xs text-dark-black">
        <span className="rounded-[6px] bg-mainColor/20 px-2 py-1 text-xs font-medium">
          {selectedProspects.length}
        </span>{" "}
        prospect
        {selectedProspects.length > 1 ? "s" : ""} selected
      </p>
      <div className="flex items-center gap-2 text-sm">
        <Button
          variant="outline"
          size="sm"
          disabled={deleting || availableMoveLists.length === 0}
          onClick={() => {
            if (availableMoveLists.length === 0) return;
            setShowMove(!showMove);
          }}
          className={`relative h-8 rounded-[6px] px-2 text-xs ${
            availableMoveLists.length === 0 ? "opacity-60 blur-[0.5px]" : ""
          }`}
        >
          {moving ? (
            <div className="flex gap-2">
              <span className="animate-spin">
                <AiOutlineLoading3Quarters size={15} />
              </span>
              <p>Moving</p>
            </div>
          ) : (
            <div className="flex">
              <span>
                <AiOutlineEnter size={15} />
              </span>
              <p>Move to</p>
            </div>
          )}
        </Button>
        {showMove && (
          <div className="absolute top-full z-50 mt-1 h-[200px] overflow-y-auto rounded-[8px] border border-light-grey bg-white p-2 text-xs shadow-lg">
            {availableMoveLists.map((list: any) => (
              <button
                key={list._id}
                onClick={() => {
                  setShowMove(false);
                  handleProspectMove(list._id);
                }}
                className="flex hover:bg-primary w-full rounded-lg gap-2 justify-center items-center p-2"
              >
                <p>{list.listName}</p>
              </button>
            ))}
          </div>
        )}
        <Button
          variant="btn-red"
          size="sm"
          disabled={deleting}
          onClick={() => {
            handleProspectDelete();
          }}
          className="h-8 min-w-[92px] whitespace-nowrap rounded-[6px] px-2 text-xs [&>span]:whitespace-nowrap"
        >
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <MdDelete size={14} className="shrink-0" />
            <span className="leading-none">Delete</span>
          </span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          // disabled={deleting}
          onClick={() => {
            setShowTags(!showTags);
          }}
          className="relative h-8 min-w-[86px] whitespace-nowrap rounded-[6px] px-2 text-xs"
        >
          {moving ? (
            <div className="flex gap-2">
              <span className="animate-spin">
                <AiOutlineLoading3Quarters size={15} />
              </span>
              <p>Moving</p>
            </div>
          ) : (
            <div className="flex">
              <span className="mr-1">+</span>
              <p>Add Tag</p>
            </div>
          )}
        </Button>
        {showTags && (
          <div className="absolute right-0 top-full z-50 mt-1 w-[220px] overflow-hidden rounded-[8px] border border-light-grey bg-white shadow-lg">
            <div className="max-h-[220px] overflow-y-auto slimScrollbar py-1">
              {tags.length === 0 ? (
                <p className="px-3 py-3 text-xs text-dark-grey">No tags available</p>
              ) : (
                tags.map((tag) => (
                  <button
                    key={tag._id}
                    onClick={() => {
                      setShowTags(false);
                      handleBulkTagging(tag._id);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-dark-black hover:bg-primary"
                  >
                    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border border-light-grey bg-white" />
                    <TagSwatch color={tag.color} size="sm" />
                    <span className="min-w-0 truncate">{tag.name}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProspectsSettings;
